'use client'

import { useState, type ReactNode, useEffect } from 'react'
import Image from 'next/image'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { SessionProvider, useSession } from 'next-auth/react'
import { Toast } from '@base-ui-components/react/toast'
import { CircleUser, X } from 'lucide-react'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'

import { I18nProvider } from '@/lib/i18n'
// import { AnalyticsProvider } from '../lib/analytics'
import Animate from '@/components/Animate'
import { ToastList } from '@/components/Toast'
import { useDrawer, useIssues } from '@/lib/state'
import { getIssues } from '@/lib/github/common'
import { ProjectWithRelations } from '@/lib/project/types'
import Issue from '@/components/Issue'
import Thread from './Thread'
// import { useAnalytics } from '../lib/analytics'

const fullTagline = 'Coding is over.'

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: session } = useSession()
  const pathname = usePathname()
  const [showTagline, setShowTagline] = useState(false)
  const [showSubtitle, setShowSubtitle] = useState(false)
  const [taglineText, setTaglineText] = useState('')

  useEffect(() => {
    // Start the animation sequence only on the home page
    if (pathname === '/') {
      setTimeout(() => setShowTagline(true), 500)
    } else {
      setTaglineText(fullTagline)
      setShowSubtitle(true)
    }
  }, [pathname])

  useEffect(() => {
    if (showTagline) {
      if (taglineText.length < fullTagline.length) {
        const timeout = setTimeout(() => {
          setTaglineText(fullTagline.slice(0, taglineText.length + 1))
        }, 60)
        return () => clearTimeout(timeout)
      } else {
        setTimeout(() => setShowSubtitle(true), 500)
      }
    }
  }, [showTagline, taglineText])
  const { rightDrawer, leftDrawer, setRightDrawer, setLeftDrawer } = useDrawer()

  return (
    <>
      {session?.user != null && (
        <Animate visible>
          {pathname === '/account' ? (
            <Link href='/chat' className='fixed top-10 right-10 z-50'>
              <X />
            </Link>
          ) : (
            <Link href='/account' className='fixed top-10 right-10 z-50000'>
              {session?.user.image ? (
                <Image
                  src={session?.user.image}
                  alt='avatar'
                  width={32}
                  height={32}
                  className='rounded-full relative z-50000'
                />
              ) : (
                <CircleUser className='h-8 w-8' />
              )}
            </Link>
          )}
        </Animate>
      )}
      <div
        className={`flex min-h-screen flex-col items-center justify-${
          pathname === '/chat' ? 'top' : 'center'
        } p-4 sm:p-8 md:p-24 bg-white`}
      >
        <div className='max-w-3xl w-full text-center'>
          <h1 className='text-4xl mb-6 sm:text-5xl md:text-6xl font-bold tracking-tight min-h-[4rem]'>
            {taglineText}
            {showSubtitle && taglineText.length < fullTagline.length && (
              <span className='animate-pulse'>|</span>
            )}
          </h1>
          {showSubtitle && children}
        </div>
        <Drawer
          open={rightDrawer != null}
          onClose={() => {
            setRightDrawer(null)
          }}
          direction='right'
          enableOverlay={false}
          style={{
            width: '340px'
          }}
        >
          {rightDrawer}
        </Drawer>
        <Drawer
          open={leftDrawer != null}
          onClose={() => {
            setLeftDrawer(null)
          }}
          direction='left'
          enableOverlay={false}
          style={{
            width: '340px'
          }}
        >
          {leftDrawer}
        </Drawer>
        {showSubtitle && (
          <footer className='text-xs fixed bottom-10 w-auto text-gray-300'>
            <Animate delay={1800}>
              &copy; {new Date().getFullYear()} Prisby. All rights reserved.
            </Animate>
          </footer>
        )}
      </div>
    </>
  )
}

export default AppLayout

export const AppProviders = ({ children }: { children: React.ReactNode }) => (
  <SessionProvider>
    {/* <AnalyticsProvider> */}
    <div className='Root'>
      <Toast.Provider>
        <I18nProvider lang={'en'}>
          <QueryListener lang={'en'}>
            {children} <ToastList />
          </QueryListener>
        </I18nProvider>
      </Toast.Provider>
    </div>
    {/* </AnalyticsProvider> */}
  </SessionProvider>
)

const QueryListener: React.FC<QueryListenerProps> = ({ children }) => {
  const { data: session, status } = useSession()
  const { push } = useRouter()
  const pathname = usePathname()
  const toastManager = Toast.useToastManager()
  const query = useSearchParams()
  const login = query.get('login')
  const threadId = query.get('threadId')
  const projectName = query.get('projectName')
  const issueNumber = query.get('issueNumber')
  const { setLeftDrawer, rightDrawer, setRightDrawer } = useDrawer()
  const { issues, setIssues, setIssuesLoading } = useIssues()
  const token = session?.user?.accounts?.[0]?.access_token ?? ''
  const project = session?.user?.projects.find(
    (p: ProjectWithRelations) => p.name === projectName
  )
  const thread = project?.threads.find(t => t.id === threadId)
  // const lang = query.get('lang')

  // const { trackPageView } = useAnalytics()

  // useEffect(() => {
  //   trackPageView({})
  // }, [asPath, trackPageView])
  useEffect(() => {
    const params = new URLSearchParams(query.toString())
    if (session?.user != null) {
      if (login) {
        toastManager.add({
          title: `Connected as ${session.user.name}`
        })
        params.delete('login')
        if (session.user.projects?.length === 0) {
          params.set('setup', '0')
        }
        window.history.pushState(null, '', `?${params.toString()}`)
      } else if (!projectName) {
        const params = new URLSearchParams(query.toString())
        if (session.user.projects?.length && session.user.projects.length > 0) {
          params.set('projectName', session.user.projects[0].name)
        }
        window.history.pushState(null, '', `?${params.toString()}`)
      }
    } else if (pathname === '/chat') {
      if (status === 'unauthenticated' && !login) {
        params.set('login', 'true')
        window.history.pushState(null, '', `?${params.toString()}`)
      }
    }
  }, [
    projectName,
    rightDrawer,
    issues,
    login,
    pathname,
    push,
    query,
    session?.user,
    setRightDrawer,
    status,
    thread?.gitHubIssue,
    threadId
  ])

  useEffect(() => {
    if (issueNumber !== 'new' && issueNumber && issues.length > 0) {
      setLeftDrawer(<Issue />)
    } else setLeftDrawer(null)
  }, [issueNumber, issues, setLeftDrawer])

  useEffect(() => {
    if (thread) {
      setRightDrawer(<Thread thread={thread} />)
    } else setRightDrawer(null)
  }, [setRightDrawer, thread])

  useEffect(() => {
    if (project && session) {
      const fetchIssues = async () => {
        setIssuesLoading({ all: true })
        const issuesResponse = await getIssues(project.repoUrl, token)
        setIssues(issuesResponse)
        setIssuesLoading({ all: false })
      }
      fetchIssues()
    }
  }, [
    project,
    session,
    session?.user.accounts,
    setIssuesLoading,
    setIssues,
    token
  ])

  return <>{children}</>
}

interface QueryListenerProps {
  children: ReactNode
  lang: string
}
